import { useEffect, useState } from "react";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Sparkles, Loader2, Heart, Share2 } from "lucide-react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { API, useAuth } from "@/App";

const BabyNameGenerator = () => {
  const { token } = useAuth();
  const [gender, setGender] = useState("any");
  const [origin, setOrigin] = useState("any");
  const [style, setStyle] = useState("any");
  const [names, setNames] = useState([]);
  const [favorites, setFavorites] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showFavorites, setShowFavorites] = useState(false);
  const headers = token ? { Authorization: `Bearer ${token}` } : {};

  const loadFavorites = async () => {
    try {
      const res = await axios.get(`${API}/wellness/baby-names/favorites`, { headers, withCredentials: true });
      setFavorites(res.data.favorites || []);
    } catch { /* silent */ }
  };

  useEffect(() => { loadFavorites(); /* eslint-disable-next-line */ }, [token]);

  const favoritedSet = new Set(favorites.map(f => f.name));

  const generate = async () => {
    setLoading(true);
    try {
      const res = await axios.post(`${API}/wellness/baby-names/generate`,
        { gender, origin, style, count: 8 },
        { headers, withCredentials: true }
      );
      setNames(res.data.names || []);
      if (!res.data.names?.length) toast.error("Couldn't generate names — try different filters.");
    } catch {
      toast.error("Could not generate names");
    } finally {
      setLoading(false);
    }
  };

  const toggleFavorite = async (n) => {
    try {
      if (favoritedSet.has(n.name)) {
        await axios.delete(`${API}/wellness/baby-names/favorite/${encodeURIComponent(n.name)}`, { headers, withCredentials: true });
        setFavorites(fs => fs.filter(f => f.name !== n.name));
        toast.success(`Removed ${n.name}`);
      } else {
        await axios.post(`${API}/wellness/baby-names/favorite`, n, { headers, withCredentials: true });
        setFavorites(fs => [{ ...n, saved_at: new Date().toISOString() }, ...fs]);
        toast.success(`${n.name} saved to favorites`);
      }
    } catch {
      toast.error("Couldn't update favorites");
    }
  };

  const shareShortlist = async () => {
    if (!favorites.length) return toast.error("No favorites to share yet");
    const text = `Our Sacred Baby Name Shortlist ✨\n\n${favorites.map((f, i) => `${i + 1}. ${f.name} — ${f.meaning || ''}`).join("\n")}`;
    try {
      if (navigator.share) {
        await navigator.share({ title: "Baby Name Shortlist", text });
      } else {
        await navigator.clipboard.writeText(text);
        toast.success("Shortlist copied to clipboard");
      }
    } catch { /* user cancelled */ }
  };

  const list = showFavorites ? favorites : names;

  return (
    <motion.div
      initial={{ opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.3 }}
      data-testid="baby-name-generator"
    >
      <Card className="glass border-purple-400/20">
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between gap-2">
            <CardTitle className="text-white flex items-center gap-2">
              <Sparkles className="w-5 h-5 text-purple-300" />
              Baby Name Inspiration
            </CardTitle>
            <div className="flex gap-1.5">
              <button
                onClick={() => setShowFavorites(false)}
                data-testid="tab-generator"
                className={`text-xs px-3 py-1.5 rounded-full ${!showFavorites ? 'bg-purple-500/30 text-white border border-purple-400/40' : 'bg-mystic-800/50 text-mystic-200 border border-white/10'}`}
              >Generate</button>
              <button
                onClick={() => setShowFavorites(true)}
                data-testid="tab-favorites"
                className={`text-xs px-3 py-1.5 rounded-full flex items-center gap-1 ${showFavorites ? 'bg-pink-500/30 text-white border border-pink-400/40' : 'bg-mystic-800/50 text-mystic-200 border border-white/10'}`}
              >
                <Heart className="w-3 h-3" /> Shortlist ({favorites.length})
              </button>
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          {!showFavorites && (
            <>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <div>
                  <Label className="text-mystic-200 text-xs">Gender</Label>
                  <Select value={gender} onValueChange={setGender}>
                    <SelectTrigger data-testid="name-gender-select" className="mt-1 bg-mystic-800/50 border-mystic-600 text-white">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="any">Any</SelectItem>
                      <SelectItem value="girl">Girl</SelectItem>
                      <SelectItem value="boy">Boy</SelectItem>
                      <SelectItem value="neutral">Gender-neutral</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-mystic-200 text-xs">Origin</Label>
                  <Select value={origin} onValueChange={setOrigin}>
                    <SelectTrigger data-testid="name-origin-select" className="mt-1 bg-mystic-800/50 border-mystic-600 text-white">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="any">Any</SelectItem>
                      <SelectItem value="english">English</SelectItem>
                      <SelectItem value="spanish">Spanish</SelectItem>
                      <SelectItem value="celtic">Celtic</SelectItem>
                      <SelectItem value="hebrew">Hebrew</SelectItem>
                      <SelectItem value="african">African</SelectItem>
                      <SelectItem value="japanese">Japanese</SelectItem>
                      <SelectItem value="arabic">Arabic</SelectItem>
                      <SelectItem value="italian">Italian</SelectItem>
                      <SelectItem value="french">French</SelectItem>
                      <SelectItem value="latin">Latin</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-mystic-200 text-xs">Style</Label>
                  <Select value={style} onValueChange={setStyle}>
                    <SelectTrigger data-testid="name-style-select" className="mt-1 bg-mystic-800/50 border-mystic-600 text-white">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="any">Any</SelectItem>
                      <SelectItem value="classic">Classic</SelectItem>
                      <SelectItem value="modern">Modern</SelectItem>
                      <SelectItem value="unique">Unique</SelectItem>
                      <SelectItem value="nature">Nature-inspired</SelectItem>
                      <SelectItem value="biblical">Biblical</SelectItem>
                      <SelectItem value="celestial">Celestial / Mystical</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <Button
                onClick={generate}
                disabled={loading}
                data-testid="generate-names-btn"
                className="w-full bg-gradient-to-r from-purple-600 to-pink-500 hover:from-purple-500 hover:to-pink-400 text-white rounded-full"
              >
                {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Sparkles className="w-4 h-4 mr-2" />}
                Generate 8 Names
              </Button>
            </>
          )}

          {showFavorites && favorites.length > 0 && (
            <Button
              onClick={shareShortlist}
              variant="outline"
              data-testid="share-shortlist-btn"
              className="w-full border-pink-400/40 text-pink-200 hover:bg-pink-500/10 rounded-full"
            >
              <Share2 className="w-4 h-4 mr-2" />
              Share my shortlist
            </Button>
          )}

          {list.length > 0 ? (
            <div className="space-y-2 pt-2" data-testid="baby-names-list">
              {list.map((n, i) => {
                const faved = favoritedSet.has(n.name);
                return (
                  <div key={`${n.name}-${i}`} className="rounded-lg bg-mystic-900/50 border border-white/5 p-3">
                    <div className="flex items-baseline justify-between gap-2 mb-1">
                      <span className="text-white text-lg font-semibold">{n.name}</span>
                      <div className="flex items-center gap-2">
                        <span className="text-mystic-200/70 text-xs">{n.origin} · {n.gender}</span>
                        <button
                          onClick={() => toggleFavorite(n)}
                          data-testid={`favorite-btn-${n.name}`}
                          aria-label={faved ? "Remove from favorites" : "Add to favorites"}
                          className="p-1 hover:scale-110 transition-transform"
                        >
                          <Heart className={`w-4 h-4 ${faved ? 'fill-pink-400 text-pink-400' : 'text-mystic-400'}`} />
                        </button>
                      </div>
                    </div>
                    <p className="text-mystic-200/80 text-sm leading-relaxed">{n.meaning}</p>
                  </div>
                );
              })}
            </div>
          ) : showFavorites ? (
            <p className="text-mystic-200/60 text-sm text-center py-6">
              No favorites yet. Tap the heart icon on any generated name to save it.
            </p>
          ) : null}
        </CardContent>
      </Card>
    </motion.div>
  );
};

export default BabyNameGenerator;
